// src/domains/sales/deals/forms/create-form/sales-deals-create-form.jsx

import { InputField, InputSelectOptions, ThousandsInputField, ToggleField } from 'components/molecules'
import { useFormikContext } from 'formik'
import { useEffect, useState } from 'react'
import { FieldArray, Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import { Alert, FormText, Button, Input, Label, Row, Col, Card, CardBody } from 'reactstrap'
import SalesDealsSchema from './sales-deals-schema'
import UserSelectSearchField from '../../organisms/user-select-search-field'
import { CREATED_DEAL_BY, CREATED_DEAL_BY_OPTIONS, SALES_METHOD_CREATE_OPTIONS, PAYMENT_METHOD, EDC_PAYMENT_METHODS, CARD_TYPE_OPTIONS, SALES_METHOD_NOTE_REQUIRED_METHODS, SALES_METHOD_NOTE_LABEL_BY_METHOD } from '../../constants'
import ReactAsyncMultiselectField from 'components/molecules/react-async-multiselect-field'
import SalesDealsApi from 'api/admins/sales-deals-api'
import SalesUsersApi from 'api/admins/sales-users-api'
import CourierSelectSearchField from '../../organisms/courier-select-search-field'
import PaymentMethodSelectSearchField from '../../organisms/payment-method-select-search-field'
import PhoneCodeSelectSearchField from '../../organisms/phone-code-select-search-field'
// import UserAddressPinpoint from '../../organisms/address-pinpoint'
import DateInputField from 'components/molecules/date-input-field'
import StringUtil from 'utils/string-util'
import LeadSourceSelectSearchField from '../../organisms/lead-source-select-search-field'
import SalesAliasSelectSearchField from '../../organisms/sales-alias-select-search-field'
import SalesNameSelectSearchField from '../../organisms/sales-name-select-search-field'

const propTypes = {
  onSubmitForm            : PropTypes.func.isRequired,
  checkUserEmailIsUnique  : PropTypes.func,
  checkUserPhoneIsUnique  : PropTypes.func,
  onValidationError       : PropTypes.func,
}

const MOBILE_SCROLL_OFFSET = 140

const REQUIRED_FIELD_LABELS = {
  user: 'Cari Customer',
  email: 'Email Customer',
  phone_code: 'Kode Negara',
  phone: 'Nomor HP Customer',
  sales_name_in_invoice: 'Nama Sales di Invoice',
  deal_by: 'Deal By',
  'assist_by_list.0': 'Assist By 1',
  sales_method: 'Metode Penjualan',
  created_deal_by: 'Create Deal By',
  product_search: 'Cari SKU',
  items: 'Items',
  courier: 'Kurir',
  down_payment: 'Down Payment',
  dp_transfer_proofs: 'Bukti Transfer DP',
  'payment_method_list.0': 'Metode Pembayaran 1',
  payment_method: 'Metode Pembayaran 1',
  card_type: 'Card Type',
}

const FIELD_ORDER = [
  'user',
  'email',
  'phone_code',
  'phone',
  'sales_name_in_invoice',
  'deal_by',
  'assist_by_list.0',
  'sales_method',
  'created_deal_by',
  'items',
  'items.*.selling_price',
  'courier',
  'down_payment',
  'dp_transfer_proofs',
  'payment_method_list.0',
  'payment_method',
  'card_type',
]

const FIELD_TARGET_ALIASES = {
  user_id: ['user'],
  items: ['items.0.selling_price', 'product_search'],
  payment_method: ['payment_method_list.0', 'payment_method'],
}

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false

  if (typeof value === 'string') {
    return value.trim().length > 0
  }

  if (typeof value === 'number') {
    return Number.isFinite(value)
  }

  if (typeof value === 'boolean') {
    return value === true
  }

  if (Array.isArray(value)) {
    return value.some(hasMeaningfulValue)
  }

  if (typeof value === 'object') {
    return Object.values(value).some(hasMeaningfulValue)
  }

  return Boolean(value)
}

const isBlank = (value) => !hasMeaningfulValue(value)

const getPathValue = (obj, path) => {
  if (!obj || !path) return undefined
  return path.split('.').reduce((acc, key) => {
    if (acc === null || acc === undefined) return undefined
    return acc[key]
  }, obj)
}

const flattenErrorPaths = (errorValue, prefix = '') => {
  if (!errorValue) return []
  if (typeof errorValue === 'string') return prefix ? [prefix] : []

  if (Array.isArray(errorValue)) {
    return errorValue.flatMap((item, index) =>
      flattenErrorPaths(item, prefix ? `${prefix}.${index}` : `${index}`)
    )
  }

  if (typeof errorValue === 'object') {
    return Object.keys(errorValue).flatMap((key) =>
      flattenErrorPaths(errorValue[key], prefix ? `${prefix}.${key}` : key)
    )
  }

  return prefix ? [prefix] : []
}

const buildTouchedFromErrors = (errorValue) => {
  if (!errorValue) return undefined
  if (typeof errorValue === 'string') return true
  if (Array.isArray(errorValue)) return errorValue.map(buildTouchedFromErrors)

  if (typeof errorValue === 'object') {
    return Object.keys(errorValue).reduce((acc, key) => {
      acc[key] = buildTouchedFromErrors(errorValue[key])
      return acc
    }, {})
  }

  return true
}

const mergeDeep = (base, next) => {
  if (Array.isArray(next)) return next
  if (!next || typeof next !== 'object') return next

  return Object.keys(next).reduce((acc, key) => {
    acc[key] = mergeDeep(base?.[key], next[key])
    return acc
  }, { ...(base || {}) })
}

const matchOrderedField = (orderedField, errorPaths) => {
  if (orderedField.includes('*')) {
    const regex = new RegExp(`^${orderedField.replace(/\./g, '\\.').replace('\\*', '\\d+')}$`)
    return errorPaths.find((path) => regex.test(path))
  }

  return errorPaths.find((path) => path === orderedField || path.startsWith(`${orderedField}.`))
}

const getFirstErrorField = (errors = {}) => {
  const paths = flattenErrorPaths(errors)
  if (!paths.length) return null

  for (const field of FIELD_ORDER) {
    const matched = matchOrderedField(field, paths)
    if (matched) return matched
  }

  return paths[0]
}

const getFieldLabel = (path) => {
  if (!path) return 'Field wajib'
  if (REQUIRED_FIELD_LABELS[path]) return REQUIRED_FIELD_LABELS[path]
  if (/^items\.\d+\.selling_price$/.test(path)) return 'Final Price item'

  if (/^assist_by_list\.\d+$/.test(path)) {
    const idx = Number(path.split('.')[1])
    return `Assist By ${idx + 1}`
  }

  if (/^payment_method_list\.\d+$/.test(path)) {
    const idx = Number(path.split('.')[1])
    return `Metode Pembayaran ${idx + 1}`
  }

  return path
    .split('.')
    .pop()
    .replace(/_/g, ' ')
}

const buildSubmitErrorSummary = (errors = {}) => {
  const paths = flattenErrorPaths(errors)
  const seen = new Set()

  return paths
    .map((path) => {
      const message = getPathValue(errors, path)
      return typeof message === 'string'
        ? message
        : `${getFieldLabel(path)} wajib diisi.`
    })
    .filter((message) => {
      if (!message || seen.has(message)) return false
      seen.add(message)
      return true
    })
}

const clearSubmitErrorTarget = () => {
  document
    .querySelectorAll('.bnk-submit-error-target')
    .forEach((el) => el.classList.remove('bnk-submit-error-target'))
}

const queryFieldTarget = (fieldName) => {
  const names = [
    fieldName,
    ...(FIELD_TARGET_ALIASES[fieldName] || []),
  ].filter(Boolean)

  for (const name of names) {
    const safeName = String(name).replace(/"/g, '\\"')
    const target =
      document.querySelector(`[data-required-field="${safeName}"]`) ||
      document.querySelector(`[name="${safeName}"]`)

    if (target) return target
  }

  return null
}

const scrollToFirstInvalidField = (fieldName) => {
  const targetEl = queryFieldTarget(fieldName)
  if (!targetEl) return

  clearSubmitErrorTarget()

  const scrollTarget =
    targetEl.closest('[data-required-field], .form-group, .mb-3, .border') ||
    targetEl

  scrollTarget.classList.add('bnk-submit-error-target')

  const top =
    scrollTarget.getBoundingClientRect().top +
    window.pageYOffset -
    MOBILE_SCROLL_OFFSET

  window.scrollTo({
    top: Math.max(top, 0),
    behavior: 'smooth',
  })

  window.setTimeout(() => {
    const focusTarget =
      targetEl.matches?.('input, select, textarea, button')
        ? targetEl
        : targetEl.querySelector?.('input, select, textarea, button, [tabindex]')

    if (focusTarget?.focus) {
      focusTarget.focus({ preventScroll: true })
    }
  }, 350)
}

const SubmitErrorSummarySyncer = ({ enabled, setSubmitErrorSummary }) => {
  const { errors } = useFormikContext()

  useEffect(() => {
    if (!enabled) return
    setSubmitErrorSummary(buildSubmitErrorSummary(errors))
  }, [enabled, errors, setSubmitErrorSummary])

  return null
}

  const splitFullName = (fullName = '') => {
  const cleaned = (fullName || '').trim().replace(/\s+/g, ' ')
  if(!cleaned) return { first: '', last: '' }
  const parts = cleaned.split(' ')
  const first = parts.shift() || ''
  const last  = parts.join(' ')
  return { first, last }
}

const buildFullName = (firstName, lastName) => {
  const first = (firstName || '').trim()
  const last  = (lastName || '').trim()
  return [ first, last ].filter(Boolean).join(' ').trim()
}

const toNumberOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const isItemComplete = (item) => {
  const n = toNumberOrNull(item?.selling_price)
  return n !== null && n > 0
}

const PaymentMethodSyncer = () => {
  const { values, setFieldValue } = useFormikContext()

  useEffect(() => {
    const list = Array.isArray(values.payment_method_list) ? values.payment_method_list : []

    const first = list.find((x) => {
      if (!x) return false
      if (typeof x === 'string') return x.trim().length > 0
      return true // object case
    }) || null

    if (values.payment_method !== first) {
      setFieldValue('payment_method', first)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.payment_method_list])

  return null
}

const getPaymentMethodValue = (paymentMethod) => {
  if (!paymentMethod) return null
  if (typeof paymentMethod === 'string') return paymentMethod.trim() || null
  return paymentMethod?.value || null
}

const isMidtransCreditCardPaymentMethod = (paymentMethod) => {
  return getPaymentMethodValue(paymentMethod) === PAYMENT_METHOD.CREDIT_CARD
}

const getDpValidationFinalPayment = (values = {}) => {
  if ((values.voucher_code || '').toString().trim()) return null

  const items = Array.isArray(values.items) ? values.items : []
  if (!items.length || !items.every(isItemComplete)) return null

  const subtotal = items.reduce((sum, item) => {
    return sum + (toNumberOrNull(item?.selling_price) || 0)
  }, 0)

  if (!(subtotal > 0)) return null

  const paymentMethod =
    values.payment_method ||
    (Array.isArray(values.payment_method_list) ? values.payment_method_list[0] : null)

  if (!isMidtransCreditCardPaymentMethod(paymentMethod)) return subtotal

  const cardType =
    paymentMethod && typeof paymentMethod === 'object'
      ? paymentMethod.card_type
      : values.card_type

  const netRate = cardType === 'amex' ? 0.97 : 0.974
  return Math.round((subtotal / netRate) * 100) / 100
}

const normalizeMidtransVirtualAccountPaymentMethod = (paymentMethod) => {
  if (!paymentMethod) return paymentMethod

  if (typeof paymentMethod === 'string') {
    return paymentMethod === PAYMENT_METHOD.MIDTRANS
      ? PAYMENT_METHOD.VIRTUAL_ACCOUNT
      : paymentMethod
  }

  if (paymentMethod?.value !== PAYMENT_METHOD.MIDTRANS) return paymentMethod

  return {
    ...paymentMethod,
    label: 'MIDTRANS (VA)',
    value: PAYMENT_METHOD.VIRTUAL_ACCOUNT,
  }
}

const normalizeMidtransVirtualAccountPaymentMethodList = (list = []) => {
  return (Array.isArray(list) ? list : []).map(normalizeMidtransVirtualAccountPaymentMethod)
}

// NEW: react state driver untuk conditional Card Type
const PaymentMethodCardTypeSyncer = ({ onPrimaryPaymentMethodChange }) => {
  const { values, setFieldValue } = useFormikContext()

  useEffect(() => {
    const methodValue = getPaymentMethodValue(values.payment_method)

    onPrimaryPaymentMethodChange(methodValue)

    // NEW: card_type hanya valid untuk EDC BRI / EDC BCA
    if (!EDC_PAYMENT_METHODS.includes(methodValue) && values.card_type) {
      setFieldValue('card_type', null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.payment_method, values.card_type])

  return null
}

const AssistBySyncer = () => {
  const { values, setFieldValue } = useFormikContext()

  useEffect(() => {
    const list = (values.assist_by_list || [])
      .map((x) => (x || '').toString().trim())
      .filter(Boolean)

    const joined = list.join(', ')

    if ((values.assist_by || '') !== joined) {
      setFieldValue('assist_by', joined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.assist_by_list])

  return null
}

  const SalesDealsCreateForm = ({
    onSubmitForm,
    checkUserEmailIsUnique = () => {},
    checkUserPhoneIsUnique = () => {},
    onValidationError = () => {},
  }) => {
  const initialValues = {
    // CUSTOMER
    use_existing_user: true,
    edit_existing_user: false,
    hide_existing_user_detail: true,

    user_id: null,
    user_detail_id: null,
    address_id: null,
    email: null,
    password: null,
    birthdate: null,

    // split name UI
    first_name: '',
    last_name: '',
    // legacy full name
    name: '',

    phone: null,
    phone_code: '+62',
    instagram_account: null,
    lead_source: null,

    complete_address: null,
    recipient_name: null,
    recipient_number: null,
    instruction: null,
    latitude: null,
    longitude: null,

    // SALES
    sales_name_in_invoice: null,
    deal_by: null,
    assist_by: null,
    assist_by_list: [''],

    // DEAL
    sales_method: null,
    sales_method_note: '',
    created_deal_by: CREATED_DEAL_BY.ADMIN,

    // ITEMS
    product_search: [],  // single-select (stored as array b/c component is multi)
    product_ids: [],     // added products (display-only)
    items: [],           // payload

    // SHIPMENT
    pickup_at: null,
    courier: null,
    shipment_cost: 0,

    // ORDER
    voucher_code: null,
    include_invoice: true,
    include_polaroid: false,
    include_love_letter: false,
    note: '',
    down_payment: null,
    dp_transfer_proofs: [],

    // PAYMENT (last)
    payment_method: null,
    payment_method_list: [''],
    card_type: null,
    payment_transfer_receipts: [],
  }
  // Google Maps pinpoint is intentionally disabled on this form.
  // Keep the UI block commented below, but do not load Google Maps script here
  // to prevent API-key/billing popups on pages that do not render the map.

  // const [ showPinpoint, setShowPinpoint ] = useState(false)
  const [ productSearchQuery, setProductSearchQuery ] = useState('')
  const [ submitErrorSummary, setSubmitErrorSummary ] = useState([])
  const [ primaryPaymentMethodValue, setPrimaryPaymentMethodValue ] = useState(null)

  // ===== User meta (details/addresses) for existing user =====
  const [ userMeta, setUserMeta ] = useState(null)
  const [ isUserMetaLoading, setIsUserMetaLoading ] = useState(false)
  const [ userMetaError, setUserMetaError ] = useState('')

  const loadOptions = async () => {
    const result = await SalesDealsApi.searchLiveProducts({query: productSearchQuery})
      .then((response) => response.data.products)
    return result
  }

  const normalize = (v) => (v ?? '').toString().trim()

  const getOptionLabel = (option) => {
    if (!option) return ''
    return [
      option.sku,
      option.condition_name || option.condition,
      option.name,
      option.spec || option.p_size,
    ]
      .filter(Boolean)
      .join(' - ')
  }

  const getProductAuthenticationProviderLabel = (product) => {
    const provider = product?.authentication_provider
    const providerLabels = {
      bababebi: 'Bababebi',
      entrupy: 'Entrupy',
      legit_grails: 'Legit Grails',
      authentication_first: 'Authentication First',
      check_by_koko_watch: 'Check by Koko Watch',
      real_authentication: 'Real Authentication'
    }

    if (!provider) return ''

    const label = providerLabels[provider] || provider
    return provider === 'check_by_koko_watch' && product?.authentication_provider_other ? `${label} - ${product.authentication_provider_other}` : label
  }

  const getProductAuthenticationStatusLabel = (product) => {
    const statusLabels = {
      not_checked: 'Not Checked',
      authentic: 'Authentic',
      not_authentic: 'Not Authentic',
      inconclusive: 'Inconclusive'
    }

    return statusLabels[product?.authentication_status] || 'Not Checked'
  }

  const getProductAuthenticationSummary = (product) => {
    const provider = getProductAuthenticationProviderLabel(product)
    const status = getProductAuthenticationStatusLabel(product)

    return provider ? `${status} (${provider})` : status
  }

  const renderProductOption = (o) => {
    if (!o) return null

    const sku = normalize(o.sku)
    const condition = normalize(o.condition_name)
    const name = normalize(o.name)
    const spec = normalize(o.spec)

    return (
      <div style={{ whiteSpace: 'normal', lineHeight: 1.3 }}>
        <div style={{ fontWeight: 600 }}>
          {sku}
        </div>
        <div style={{ fontSize: 13, opacity: 0.85 }}>
          {[condition, name, spec]
            .filter(Boolean)
            .join(' – ')}
        </div>
        <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
          Authentication: {getProductAuthenticationSummary(o)}
        </div>
      </div>
    )
  }

  const syncFullNameIntoLegacyName = (formikBag, firstName, lastName) => {
    formikBag.setFieldValue('name', buildFullName(firstName, lastName))
  }

  const addSelectedProductAsItem = (formikBag) => {
    const selected = (formikBag.values.product_search || [])[0]
    if(!selected) return

    const alreadyAdded = (formikBag.values.product_ids || []).some(p => p.id === selected.id)
    if(alreadyAdded){
      alert(`Item sudah ditambahkan: ${selected.sku}`)
      return
    }

    // default Final Price = product.selling_price, fallback to default_price
    const defaultFinalPrice =
      (selected.selling_price !== null && selected.selling_price !== undefined && selected.selling_price !== '')
        ? selected.selling_price
        : selected.default_price

    const newItem = {
      product_id: selected.id,
      selling_price: defaultFinalPrice || '',
      note: '',
    }

    formikBag.setFieldValue('product_ids', [ ...(formikBag.values.product_ids || []), selected ])
    formikBag.setFieldValue('items', [ ...(formikBag.values.items || []), newItem ])

    // clear search
    formikBag.setFieldValue('product_search', [])
    setProductSearchQuery('')
  }

  const BlackSectionHeader = ({ title, subtitle }) => (
    <div
      style={{
        background: '#000',
        color: '#fff',
        padding: '12px 16px',
        borderRadius: 6,
        marginBottom: 16,
      }}
    >
      <div style={{ fontWeight: 800, letterSpacing: 0.3 }}>{title}</div>
      {subtitle && (
        <div style={{ fontSize: 12, opacity: 0.9, marginTop: 4 }}>
          {subtitle}
        </div>
      )}
    </div>
  )

  const SectionTitle = ({ title, subtitle }) => (
    <div style={{ marginBottom: 10 }}>
      <h4 style={{ marginBottom: 6 }}>{title}</h4>
      {subtitle && <FormText style={{ marginTop: 0 }}>{subtitle}</FormText>}
    </div>
  )

    const getUserAddressPageLink = (userId) => (
    userId ? `/app/sales/users/${userId}` : '/app/sales/users'
  )

  const PinpointMovedNote = ({ userId }) => (
    <Alert color='warning' style={{ marginTop: 12, marginBottom: 16 }}>
      <div>
        Set Google Map / Pinpoint sudah tidak di halaman Deals, silakan set di <b>User Address</b>.
      </div>

      <div style={{ marginTop: 6 }}>
        <a
          href={getUserAddressPageLink(userId)}
          target='_blank'
          rel='noopener noreferrer'
        >
          Klik edit di sini
        </a>
      </div>

      {!userId && (
        <FormText className='text-muted' style={{ marginTop: 8 }}>
          Pilih customer terlebih dahulu agar link mengarah langsung ke user yang dipilih.
        </FormText>
      )}
    </Alert>
  )

  // ===== User detail/address helpers (for existing user selection) =====
  const pickPrimary = (arr) => {
    const list = Array.isArray(arr) ? arr.filter(Boolean) : []
    if (!list.length) return null
    return list.find(x => x?.primary) || list[0]
  }

  const getDetailLabel = (d) => {
    if (!d) return '-'
    const name = (d.name || [d.first_name, d.last_name].filter(Boolean).join(' ')).trim()
    const phone = (d.phone || '').toString().trim()
    return [name, phone].filter(Boolean).join(' - ') || `Detail #${d.id}`
  }

  const getAddressLabel = (a) => {
    if (!a) return '-'
    const addr = (a.complete_address || '').toString().trim()
    if (addr) return addr.length > 80 ? `${addr.slice(0, 80)}…` : addr
    const rec = [a.recipient_name, a.recipient_number].filter(Boolean).join(' / ')
    return rec ? `(${rec}) Address #${a.id}` : `Address #${a.id}`
  }

  const applyUserDetailToFormik = (detail, formikBag) => {
    if (!detail || !formikBag) return
    formikBag.setFieldValue('user_detail_id', detail.id)

    const firstName = (detail.first_name || '').toString()
    const lastName  = (detail.last_name || '').toString()
    const fullName  = (detail.name || '').toString()

    if (firstName || lastName) {
      formikBag.setFieldValue('first_name', firstName)
      formikBag.setFieldValue('last_name', lastName)
      syncFullNameIntoLegacyName(formikBag, firstName, lastName)
    } else if (fullName) {
      const split = splitFullName(fullName)
      formikBag.setFieldValue('first_name', split.first_name)
      formikBag.setFieldValue('last_name', split.last_name)
      syncFullNameIntoLegacyName(formikBag, split.first_name, split.last_name)
    }

    if (detail.phone !== undefined) {
      formikBag.setFieldValue('phone', detail.phone || '')
    }
  }

  const applyUserAddressToFormik = (addr, formikBag) => {
    if (!addr || !formikBag) return
    formikBag.setFieldValue('address_id', addr.id)

    formikBag.setFieldValue('complete_address', addr.complete_address || '')
    formikBag.setFieldValue('recipient_name', addr.recipient_name || '')
    formikBag.setFieldValue('recipient_number', addr.recipient_number || '')
    formikBag.setFieldValue('instruction', addr.instruction || '')

    if (addr.latitude !== undefined) formikBag.setFieldValue('latitude', addr.latitude)
    if (addr.longitude !== undefined) formikBag.setFieldValue('longitude', addr.longitude)
  }

  const fetchAndApplyUserMeta = async (userId, formikBag, preferredDetailId = null) => {
    if (!userId) {
      setUserMeta(null)
      setUserMetaError('')
      return
    }

    setIsUserMetaLoading(true)
    setUserMetaError('')

    try {
      const response = await SalesUsersApi.show({ userId })
      const fullUser = response?.data?.user || response?.data?.data || response?.data
      setUserMeta(fullUser || null)

      const details = Array.isArray(fullUser?.details) ? fullUser.details : []
      const addresses = Array.isArray(fullUser?.addresses) ? fullUser.addresses : []

      // ✅ prefer detail from search result
      const preferred =
        preferredDetailId
          ? details.find(d => String(d?.id) === String(preferredDetailId))
          : null

      const defaultDetail = preferred || pickPrimary(details)
      const defaultAddress = pickPrimary(addresses)

      if (defaultDetail) applyUserDetailToFormik(defaultDetail, formikBag)
      if (defaultAddress) applyUserAddressToFormik(defaultAddress, formikBag)
    } catch (err) {
      console.error(err)
      setUserMeta(null)
      setUserMetaError(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load user meta'
      )
    } finally {
      setIsUserMetaLoading(false)
    }
  }

  return (
    <Formik
      initialValues={ initialValues }
      validationSchema={ SalesDealsSchema }
      validate={(values) => {
        const errors = {}
        const methodValue = getPaymentMethodValue(values.payment_method)
        const isMidtransCC = isMidtransCreditCardPaymentMethod(values.payment_method)
        const dp = toNumberOrNull(values.down_payment)
        const files = values.dp_transfer_proofs || []

        const items = values.items || []
        const firstAssistBy = (values.assist_by_list || [])[0]
        const firstPaymentMethod = (values.payment_method_list || [])[0]

        if (values.use_existing_user && !values.user_id) {
          errors.user = 'Pilih customer terlebih dahulu.'
        }

        if (!values.use_existing_user) {
          if (isBlank(values.email)) {
            errors.email = 'Email Customer wajib diisi.'
          }

          if (isBlank(values.phone_code)) {
            errors.phone_code = 'Kode Negara wajib dipilih.'
          }

          if (isBlank(values.phone)) {
            errors.phone = 'Nomor HP Customer wajib diisi.'
          }
        }

        if (isBlank(values.sales_name_in_invoice)) {
          errors.sales_name_in_invoice = 'Nama Sales di Invoice wajib dipilih.'
        }

        if (isBlank(values.deal_by)) {
          errors.deal_by = 'Deal By wajib dipilih.'
        }

        if (isBlank(firstAssistBy)) {
          errors.assist_by_list = ['Assist By 1 wajib dipilih.']
        }

        if (isBlank(values.sales_method)) {
          errors.sales_method = 'Metode Penjualan wajib dipilih.'
        }

        if (isBlank(values.created_deal_by)) {
          errors.created_deal_by = 'Create Deal By wajib dipilih.'
        }

        if (!items.length) {
          errors.items = 'Tambahkan minimal 1 item ke deal.'
        } else {
          const itemErrors = []
          items.forEach((item, index) => {
            if (!isItemComplete(item)) {
              itemErrors[index] = {
                selling_price: 'Final Price wajib diisi dan harus lebih dari 0.',
              }
            }
          })
          if (itemErrors.length > 0) errors.items = itemErrors
        }

        if (items.length > 0 && items.every(isItemComplete) && isBlank(values.courier)) {
          errors.courier = 'Kurir wajib dipilih.'
        }

        if (!isBlank(values.courier) && isBlank(firstPaymentMethod)) {
          errors.payment_method_list = ['Metode Pembayaran 1 wajib dipilih.']
        }

        if (EDC_PAYMENT_METHODS.includes(methodValue) && !values.card_type) {
          errors.card_type = 'Card Type wajib dipilih untuk EDC BRI / EDC BCA.'
        }

        // DP amount: optional for MIDTRANS (CC), required for others
        if (isMidtransCC) {
          if (dp !== null && dp <= 0) {
            errors.down_payment = 'Jika diisi, nominal DP harus lebih dari 0.'
          }
        } else {
          if (dp === null || dp <= 0) {
            errors.down_payment = 'Mohon Isi Down Payment (DP).'
          }
        }

        const finalPaymentForDpValidation = getDpValidationFinalPayment(values)
        if (
          dp !== null &&
          finalPaymentForDpValidation !== null &&
          dp > finalPaymentForDpValidation
        ) {
          errors.down_payment = 'Down Payment tidak boleh lebih besar dari Final Payment.'
        }

        // DP proof: optional for MIDTRANS (CC), required for others
        if (!isMidtransCC && (!files || files.length === 0)) {
          errors.dp_transfer_proofs = 'Upload bukti transfer DP.'
        }

        return errors
      }}
            onSubmit={ (values, formikBag) => onSubmitForm({
        ...values,
        shipment_cost: toNumberOrNull(values.shipment_cost) ?? 0,
        payment_method: normalizeMidtransVirtualAccountPaymentMethod(values.payment_method),
        payment_method_list: normalizeMidtransVirtualAccountPaymentMethodList(values.payment_method_list),
      }, formikBag) }
    >
      {(formikBag) => {
        const addNewUser = !formikBag.values.use_existing_user

        const items = formikBag.values.items || []
        const canAddNewItem = items.length === 0 || items.every(isItemComplete)

        const allItemsComplete = items.length > 0 && items.every(isItemComplete)
        const hasAtLeastOneItem = items.length > 0
        const showCardType = EDC_PAYMENT_METHODS.includes(primaryPaymentMethodValue)
        const isMidtransCCSelected = isMidtransCreditCardPaymentMethod(formikBag.values.payment_method)

        const selectedToAdd = (formikBag.values.product_search || [])[0]
        const canClickAdd = !!selectedToAdd && canAddNewItem

        const hasSelectedButNotAdded =
          (formikBag.values.product_search || []).length > 0 &&
          !formikBag.values.items?.some(i => i.product_id === selectedToAdd?.id)

        const shouldShowSalesMethodNote = (salesMethod) => {
          return SALES_METHOD_NOTE_REQUIRED_METHODS.includes(salesMethod)
        }

        const getSalesMethodNoteLabel = (salesMethod) => {
          return SALES_METHOD_NOTE_LABEL_BY_METHOD[salesMethod] || 'Sales Method Note'
        }

        return (
          <>
            <style>
              {`
                .bnk-submit-error-target {
                  border: 1px solid #dc3545 !important;
                  border-radius: 6px;
                  padding: 10px;
                  background: #fff5f5;
                  scroll-margin-top: ${MOBILE_SCROLL_OFFSET}px;
                }

                @media (max-width: 480px) {
                  .bnk-submit-error-target {
                    padding: 8px;
                  }
                }
              `}
            </style>

            <PaymentMethodSyncer />
            <PaymentMethodCardTypeSyncer onPrimaryPaymentMethodChange={ setPrimaryPaymentMethodValue } />
            <AssistBySyncer />
            <SubmitErrorSummarySyncer
              enabled={submitErrorSummary.length > 0}
              setSubmitErrorSummary={setSubmitErrorSummary}
            />

            <Form onSubmit={(e) => e.preventDefault()}>

              {/* =========================
                    QUICK GUIDE
                 ========================= */}
              <Alert color="info" style={{ marginBottom: 18 }}>
                <b>Urutan Pengisian (biar nggak nyasar):</b><br/>
                1) Data Customer → 2) Deal By/Assist By → 3) Metode Penjualan → 4) Create Deal By →<br/>
                5) Cari SKU → <b>Klik Add Item</b> → 6) Isi Final Price → 7) Kurir/Pickup → 8) Metode Pembayaran
              </Alert>

              {/* =========================
                    1) CUSTOMER
                 ========================= */}
              <BlackSectionHeader
                title="DATA CUSTOMER"
                subtitle={<>⚠️ Semua data di bawah ini adalah data <b>PEMBELI</b>, bukan data Sales.</>}
              />

              <SectionTitle
                title="Customer"
                subtitle={<>Pilih apakah customer sudah pernah terdaftar di sistem atau customer baru.</>}
              />

              <ToggleField
                type='checkbox'
                name='use_existing_user'
                label='Customer sudah terdaftar di sistem?'
                onChange={() => {
                  if(formikBag.values.use_existing_user){
                    // switch to NEW customer
                    formikBag.setFieldValue('use_existing_user', false)

                    formikBag.setFieldValue('user', null)
                    formikBag.setFieldValue('user_id', null)
                    formikBag.setFieldValue('email', '')
                    formikBag.setFieldValue('password', '')
                    formikBag.setFieldValue('birthdate', null)

                    formikBag.setFieldValue('first_name', '')
                    formikBag.setFieldValue('last_name', '')
                    formikBag.setFieldValue('name', '')

                    formikBag.setFieldValue('phone_code', null)
                    formikBag.setFieldValue('phone', '')
                    formikBag.setFieldValue('instagram_account', '')
                    formikBag.setFieldValue('lead_source', '')
                    formikBag.setFieldValue('complete_address', '')
                    formikBag.setFieldValue('recipient_name', '')
                    formikBag.setFieldValue('recipient_number', '')
                    formikBag.setFieldValue('instruction', '')
                    formikBag.setFieldValue('latitude', '')
                    formikBag.setFieldValue('longitude', '')

                    // reset selections that depend on customer
                    formikBag.setFieldValue('courier', null)
                    formikBag.setFieldValue('shipment_cost', 0)
                    formikBag.setFieldValue('payment_method_list', [''])
                    formikBag.setFieldValue('payment_method', null)
                    formikBag.setFieldValue('card_type', null)
                    formikBag.setFieldValue('payment_transfer_receipts', [])
                  } else {
                    // switch to EXISTING customer
                    formikBag.setFieldValue('use_existing_user', true)
                  }
                }}
              />

              <FormText style={{ marginTop: -8, marginBottom: 16 }}>
                • <b>YA</b> = cari customer yang sudah ada<br/>
                • <b>TIDAK</b> = input data customer baru
              </FormText>

              {formikBag.values.use_existing_user && (
                <>
                  <div data-required-field='user'>
                    <UserSelectSearchField
                      name='user'
                      label='Cari Customer (email / nama / nomor HP)'
                      onSelect={(selectedUser) => {
                      const fullName = selectedUser?.name || ''
                      const { first, last } = splitFullName(fullName)

                      formikBag.setFieldValue('user_id', selectedUser.id)
                      formikBag.setFieldValue('email', selectedUser.email)
                      formikBag.setFieldValue('birthdate', selectedUser.birthdate)

                      formikBag.setFieldValue('first_name', first)
                      formikBag.setFieldValue('last_name', last)
                      formikBag.setFieldValue('name', buildFullName(first, last) || fullName)

                      formikBag.setFieldValue('phone_code', selectedUser.phone_code)
                      formikBag.setFieldValue('phone', selectedUser.phone)
                      formikBag.setFieldValue('instagram_account', selectedUser.instagram_account)
                      formikBag.setFieldValue('lead_source', selectedUser.lead_source)
                      formikBag.setFieldValue('complete_address', selectedUser.complete_address)
                      formikBag.setFieldValue('recipient_name', selectedUser.recipient_name)
                      formikBag.setFieldValue('recipient_number', selectedUser.recipient_number)
                      formikBag.setFieldValue('instruction', selectedUser.instruction)
                      formikBag.setFieldValue('latitude', selectedUser.latitude)
                      formikBag.setFieldValue('longitude', selectedUser.longitude)
                      // Load user details/addresses for selector
                      fetchAndApplyUserMeta(selectedUser.id, formikBag, selectedUser.user_detail_id)
                      }}
                    />
                  </div>

                  {/* ===== Detail/Address selector (loaded from SalesUsersApi.show) ===== */}
                  {
                    formikBag.values.use_existing_user === true &&
                    formikBag.values.user_id &&
                    (
                      <Row className="mt-3">
                        <Col md="6">
                          <Card className="mb-3">
                            <CardBody>
                              <h5 className="mb-2">Customer Detail (UserDetail)</h5>

                              <Label className="font-weight-bold">Pilih Detail</Label>
                              <Input
                                type="select"
                                value={formikBag.values.user_detail_id || ''}
                                disabled={isUserMetaLoading || !userMeta}
                                onChange={(e) => {
                                  const id = e.target.value ? parseInt(e.target.value, 10) : null
                                  formikBag.setFieldValue('user_detail_id', id)
                                  const detail = (userMeta?.details || []).find(d => d?.id === id)
                                  if (detail) applyUserDetailToFormik(detail, formikBag)
                                }}
                              >
                                <option value="" disabled>-- pilih detail --</option>
                                {(userMeta?.details || []).map((d) => (
                                  <option key={d.id} value={d.id}>
                                    {getDetailLabel(d)}
                                  </option>
                                ))}
                              </Input>

                              {isUserMetaLoading && (
                                <FormText className="text-muted">Loading user details…</FormText>
                              )}

                              {userMetaError && (
                                <Alert color="danger" className="mt-2">
                                  {userMetaError}
                                </Alert>
                              )}

                              {!isUserMetaLoading && userMeta && (userMeta?.details || []).length === 0 && (
                                <Alert color="warning" className="mt-2">
                                  User ini belum punya detail. Buat detail dulu di halaman User.
                                </Alert>
                              )}
                            </CardBody>
                          </Card>
                        </Col>

                        <Col md="6">
                          <Card className="mb-3">
                            <CardBody>
                              <h5 className="mb-2">Shipping Address</h5>

                              <Label className="font-weight-bold">Pilih Address</Label>
                              <Input
                                type="select"
                                value={formikBag.values.address_id || ''}
                                disabled={isUserMetaLoading || !userMeta}
                                onChange={(e) => {
                                  const id = e.target.value ? parseInt(e.target.value, 10) : null
                                  formikBag.setFieldValue('address_id', id)
                                  const addr = (userMeta?.addresses || []).find(a => a?.id === id)
                                  if (addr) applyUserAddressToFormik(addr, formikBag)
                                }}
                              >
                                <option value="" disabled>-- pilih address --</option>
                                {(userMeta?.addresses || []).map((a) => (
                                  <option key={a.id} value={a.id}>
                                    {getAddressLabel(a)}
                                  </option>
                                ))}
                              </Input>

                              <FormText className="text-muted">
                                Memilih address akan mengisi otomatis field alamat + koordinat (jika ada).
                              </FormText>

                              {!isUserMetaLoading && userMeta && (userMeta?.addresses || []).length === 0 && (
                                <Alert color="warning" className="mt-2">
                                  User ini belum punya address. Buat address dulu di halaman User.
                                </Alert>
                              )}
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    )
                  }

                  <ToggleField
                    type="checkbox"
                    name="hide_existing_user_detail"
                    label="Sembunyikan detail customer (biar ringkas)"
                  />
                  <FormText style={{ marginTop: -8, marginBottom: 16 }}>
                    Jika dimatikan, kamu bisa melihat & mengubah data customer.
                  </FormText>
                </>
              )}

              {
                (
                  (formikBag.values.use_existing_user && formikBag.values.user_id)
                  || addNewUser
                )
                &&
                <>
                  {(addNewUser || formikBag.values.hide_existing_user_detail === false) && (
                    <>
                      <InputField
                        type='text'
                        name='email'
                        label='Email Customer'
                        required={ true }
                        formText='⚠️ Pastikan ini email CUSTOMER, bukan email Sales.'
                        onBlur={(e) => {
                          if(addNewUser){
                            checkUserEmailIsUnique(e.target.value)
                          }
                        }}
                      />

                      <InputField
                        type='password'
                        name='password'
                        label='Password Customer (opsional)'
                        formText='Jika password tidak diisi, akan diisi random oleh sistem. Password harus 8+ karakter, huruf kecil dan besar, simbol.'
                      />

                      <InputField type='date' label='Tanggal Lahir (opsional)' name='birthdate' />

                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 240 }}>
                          <InputField
                            type='text'
                            name='first_name'
                            label='Nama Depan'
                            onChange={(e) => {
                              const v = e.target.value
                              formikBag.setFieldValue('first_name', v)
                              syncFullNameIntoLegacyName(formikBag, v, formikBag.values.last_name)
                            }}
                          />
                        </div>

                        <div style={{ flex: 1, minWidth: 240 }}>
                          <InputField
                            type='text'
                            name='last_name'
                            label='Nama Belakang'
                            onChange={(e) => {
                              const v = e.target.value
                              formikBag.setFieldValue('last_name', v)
                              syncFullNameIntoLegacyName(formikBag, formikBag.values.first_name, v)
                            }}
                          />
                        </div>
                      </div>

                      <PhoneCodeSelectSearchField
                        name='phone_code'
                        label='Kode Negara (Phone Code)'
                        required={ true }
                        onSelect={(selectedOption) => {
                          formikBag.setFieldValue('phone_code', selectedOption.phone_code)
                          if(addNewUser){
                            checkUserPhoneIsUnique(selectedOption.phone_code + formikBag.values.phone)
                          }
                        }}
                      />
                      <FormText style={{marginTop: '-20px', marginBottom: '20px', fontWeight: 'bold'}}>
                        Jika belum ada tanda X, berarti belum dipilih.
                      </FormText>

                      <InputField
                        type='text'
                        name='phone'
                        required={ true }
                        label='Nomor HP Customer'
                        placeholder='8123456789'
                        formText='Isi nomor HP tanpa spasi/tanda baca. Jangan masukkan phone code (+62).'
                        onBlur={(e) => {
                          const filteredPhone = (e.target.value || '').replace(/\D/g, '')
                          formikBag.setFieldValue('phone', filteredPhone)
                          if(addNewUser){
                            checkUserPhoneIsUnique(formikBag.values.phone_code + filteredPhone)
                          }
                        }}
                      />

                      <InputField
                        type='text'
                        name='instagram_account'
                        label='Instagram Customer (opsional)'
                        placeholder='@username'
                      />

                      <LeadSourceSelectSearchField
                        name='lead_source'
                        label='Customer tahu Belle & Kate dari mana? (opsional)'
                        required={ false }
                      />
                    </>
                  )}

                  <SectionTitle
                    title='Alamat Pengiriman'
                    subtitle={<>Isi alamat customer. Koordinat membantu menampilkan opsi kurir lebih lengkap.</>}
                  />

                  <InputField type='text' name='complete_address' label='Alamat Lengkap' />
                  <InputField type='text' name='recipient_name' label='Nama Penerima' />
                  <InputField
                    type='text'
                    name='recipient_number'
                    label='Nomor Penerima'
                    placeholder='+628123456789'
                    formText='Phone code perlu dimasukkan di sini (contoh: +62...)'
                  />
                  <InputField type='text' name='instruction' label='Instruksi Tambahan (opsional)' placeholder='Misal: titip resepsionis' />

                  <PinpointMovedNote userId={formikBag.values.user_id} />

                  {/* Render Safe Google Maps */}
                  {/* {loadError && (
                    <Alert color="danger">
                      Gagal load Google Maps. Silakan refresh atau hubungi tim IT.
                    </Alert>
                  )}

                  {!gmapLoaded && !loadError && (
                    <FormText>Loading Google Maps...</FormText>
                  )}

                  {gmapLoaded && !showPinpoint && (
                    <Button
                      color='primary'
                      onClick={() => setShowPinpoint(true)}
                      style={{ marginBottom: 10 }}
                    >
                      {'📍 Pilih Koordinat (opsional)'}
                    </Button>
                  )}

                  {gmapLoaded && showPinpoint && (
                    <UserAddressPinpoint
                      lat={ parseFloat(formikBag.values.latitude) }
                      long={ parseFloat(formikBag.values.longitude) }
                      setFieldValue={ formikBag.setFieldValue }
                      closePinpoint={ () => { setShowPinpoint(false) } }
                    />
                  )} */}
                </>
              }

              {formikBag.values.use_existing_user && formikBag.values.user_id && (
                <>
                  <ToggleField type='checkbox' name='edit_existing_user' label='Simpan perubahan data customer' />
                  <FormText style={{ marginTop: -8 }}>
                    Nyalakan hanya jika kamu memang mengubah data customer dan ingin disimpan.
                  </FormText>
                </>
              )}

              <hr />

              {/* =========================
                    2) DEAL BY / ASSIST BY
                 ========================= */}
              <SectionTitle
                title='Deal By / Assist By'
                subtitle={<>• <b>Deal By</b> = Sales yang closing • <b>Assist By</b> = Sales yang membantu</>}
              />

              <div data-required-field='sales_name_in_invoice'>
                <SalesAliasSelectSearchField
                  name='sales_name_in_invoice'
                  label='Nama Sales di Invoice'
                  required={ true }
                />
              </div>

              <div data-required-field='deal_by'>
                <SalesNameSelectSearchField
                  name='deal_by'
                  label='Deal By'
                  required={ true }
                />
              </div>

              <FieldArray
                name='assist_by_list'
                render={(arrayHelpers) => {
                  const list = formikBag.values.assist_by_list || ['']

                  return (
                    <div className='mt-2'>
                      <Label style={{ fontWeight: 700 }}>Assist By (bisa lebih dari 1)</Label>
                      <FormText className='text-muted mb-2'>
                        Isi minimal 1. Klik “Tambah Assist” untuk menambah assisting.
                      </FormText>

                      {list.map((_, idx) => {
                        const fieldName = `assist_by_list.${idx}`
                        const isFirst = idx === 0

                        return (
                          <div
                            key={fieldName}
                            style={{
                              border: '1px solid #eee',
                              borderRadius: 6,
                              padding: 12,
                              marginBottom: 10,
                              background: '#fafafa',
                            }}
                          >
                            <SalesNameSelectSearchField
                              name={fieldName}
                              label={`Assist By ${idx + 1}`}
                              required={isFirst}
                            />

                            {!isFirst && (
                              <Button
                                type='button'
                                color='danger'
                                size='sm'
                                onClick={() => {
                                  arrayHelpers.remove(idx)

                                  const next = (formikBag.values.assist_by_list || []).filter((_, i) => i !== idx)
                                  if (next.length === 0) arrayHelpers.push('')
                                }}
                              >
                                Hapus Assist
                              </Button>
                            )}
                          </div>
                        )
                      })}

                      <Button
                        type='button'
                        color='secondary'
                        onClick={() => arrayHelpers.push('')}
                      >
                        + Tambah Assist
                      </Button>
                    </div>
                  )
                }}
              />

              <hr />

              {/* =========================
                    3) METODE PENJUALAN
                 ========================= */}
              <SectionTitle
                title='Metode Penjualan'
                subtitle={<>Pilih metode penjualan yang paling sesuai dengan konteks deal (misal Regular Purchase).</>}
              />

              <InputField
                type='select'
                name='sales_method'
                label='Metode Penjualan'
                required={ true }
                formText='Pilih Regular Purchase apabila customer membeli diluar event apapun'
              >
                <InputSelectOptions options={ SALES_METHOD_CREATE_OPTIONS } keyPrefix={ 'sales_method' } />
              </InputField>

              {shouldShowSalesMethodNote(formikBag.values.sales_method) && (
                <InputField
                  type='text'
                  name='sales_method_note'
                  label={getSalesMethodNoteLabel(formikBag.values.sales_method)}
                  required={ true }
                  placeholder='Contoh: Nama influencer / akun Instagram'
                  formText='Isi nama influencer / akun yang menjadi sumber deal ini.'
                />
              )}

              <hr />

              {/* =========================
                    4) CREATE DEAL BY
                 ========================= */}
              <SectionTitle
                title='Create Deal By'
                subtitle={<>Tentukan sumber pembuatan deal. Default <b>Admin</b> untuk deal dari dashboard.</>}
              />

              <InputField
                type='select'
                name='created_deal_by'
                label='Create Deal By'
                required={ true }
                formText='Default Admin untuk deal yang dibuat oleh dashboard. Pilih Web hanya jika deal perlu ditandai sebagai berasal dari website.'
              >
                <InputSelectOptions options={ CREATED_DEAL_BY_OPTIONS } keyPrefix={ 'created_deal_by' } />
              </InputField>

              <hr />

              {/* =========================
                    5) ITEMS (ADD/DELETE)
                 ========================= */}
              <SectionTitle
                title='Items'
                subtitle={<>Tambahkan item satu per satu. Setelah Final Price terisi, baru bisa tambah item berikutnya.</>}
              />

              {!canAddNewItem && (
                <Alert color='warning' style={{ marginTop: 10 }}>
                  ⚠️ Kamu belum bisa tambah item baru — lengkapi <b>Final Price</b> untuk semua item yang sudah ada.
                </Alert>
              )}

              <Alert color='primary' style={{ marginTop: 10 }}>
                <b>Petunjuk:</b> 1) Cari SKU → 2) Klik <b>➕ Add Item ke Deal</b><br/>
                Kalau cuma pilih SKU tanpa klik Add Item, item belum masuk ke deal.
              </Alert>

              <div style={{ opacity: canAddNewItem ? 1 : 0.6, pointerEvents: canAddNewItem ? 'auto' : 'none' }}>
                <div data-required-field='product_search'>
                  <ReactAsyncMultiselectField
                    name='product_search'
                    label='Cari SKU'
                    placeholder='Cari dgn nama atau SKU'
                    required={false}
                    loadOptions={loadOptions}
                    setInputValue={setProductSearchQuery}
                    getOptionLabel={getOptionLabel}
                    formatOptionLabel={renderProductOption}
                    handleCustomChange={(options) => {
                    const one = Array.isArray(options) ? options.slice(-1) : []
                    formikBag.setFieldValue('product_search', one)
                    }}
                    dataSelected={formikBag.values.product_search}
                    selectStyles={{
                    option: (base) => ({
                      ...base,
                      whiteSpace: 'normal',
                      lineHeight: 1.3,
                      alignItems: 'flex-start',
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      whiteSpace: 'normal',
                      flexWrap: 'wrap',
                    }),
                    singleValue: (base) => ({
                      ...base,
                      whiteSpace: 'normal',
                      maxWidth: '100%',
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>

                {hasSelectedButNotAdded && (
                  <FormText className='text-warning' style={{ marginTop: 6 }}>
                    ⚠️ SKU sudah dipilih, tapi belum ditambahkan. Klik tombol <b>➕ Add Item ke Deal</b>.
                  </FormText>
                )}

                <Button
                  color='primary'
                  type='button'
                  size='lg'
                  style={{ marginTop: 10 }}
                  disabled={!canClickAdd}
                  onClick={() => addSelectedProductAsItem(formikBag)}
                >
                  ➕ Add Item ke Deal
                </Button>

                {!selectedToAdd && (
                  <FormText className='text-muted' style={{ marginTop: 6 }}>
                    Pilih SKU dulu agar tombol Add Item aktif.
                  </FormText>
                )}
              </div>

              <hr />

              {/* =========================
                    5) DETAIL ITEMS
                 ========================= */}
              <SectionTitle
                title='Detail Items'
                subtitle={<>• <b>Open Price</b> = harga awal (read-only) • <b>Final Price</b> = harga deal setelah nego</>}
              />

              <FormText>
                Metode Penjualan (deal): <b>{formikBag.values.sales_method || '-'}</b>
              </FormText>

              <FieldArray
                name='items'
                render={ (arrayHelpers) => (
                  <>
                    {items.length === 0 && (
                      <FormText className='text-muted'>
                        Belum ada item. Tambahkan item dari pencarian SKU di atas, lalu klik <b>Add Item</b>.
                      </FormText>
                    )}

                    {items.length > 0 && (
                      <div className='mt-3'>
                        {items.map((item, index) => {
                          const product = (formikBag.values.product_ids || []).find(p => p.id === item.product_id)

                          const title = product ? getOptionLabel(product) : `Product #${item.product_id}`

                          const openN = toNumberOrNull(product?.selling_price)
                          const finalN = toNumberOrNull(item?.selling_price)
                          const diff = (openN !== null && finalN !== null) ? (openN - finalN) : null

                          return (
                            <div key={item.product_id} className='border rounded p-3 mb-3'>
                              <h5 className='mb-3'>
                                {index + 1}. {title}
                              </h5>

                              <Alert color={product?.authentication_status === 'authentic' ? 'success' : product?.authentication_status === 'not_authentic' ? 'danger' : 'warning'} className='py-2'>
                                <b>Authentication Status:</b> {getProductAuthenticationSummary(product)}
                                {product?.authentication_certificate_link ? (
                                  <span> — <a href={product.authentication_certificate_link} target='_blank' rel='noopener noreferrer'>Open cert/link</a></span>
                                ) : null}
                                {product?.authentication_notes ? (
                                  <div className='mt-1'>Notes: {product.authentication_notes}</div>
                                ) : null}
                              </Alert>

                              <div className='mb-3'>
                                <Label className='form-control-label'>Open Price</Label>
                                <Input
                                  type='text'
                                  value={openN === null ? '-' : StringUtil.currencyCase(openN)}
                                  disabled
                                />
                                <FormText>Harga awal dari sistem (tidak bisa diubah)</FormText>
                              </div>

                              <ThousandsInputField
                                name={`items.${index}.selling_price`}
                                label='Final Price (Wajib)'
                                placeholder='Masukkan harga deal (nego)'
                                required={ true }
                                formText={
                                  finalN === null
                                    ? '⚠️ Isi Final Price agar bisa lanjut tambah item / pilih kurir'
                                    : `Selisih (Open - Final): ${diff === null ? '-' : StringUtil.currencyCase(diff)}`
                                }
                              />

                              <InputField
                                type='text'
                                name={`items.${index}.note`}
                                label='Catatan per Item (opsional)'
                                formText='Isi catatan apapun yang berkaitan dengan product di sini'
                              />

                              <Button
                                color='danger'
                                size='sm'
                                type='button'
                                onClick={() => {
                                  arrayHelpers.remove(index)

                                  const newSelected =
                                    (formikBag.values.product_ids || []).filter(
                                      (p) => p.id !== item.product_id
                                    )
                                  formikBag.setFieldValue('product_ids', newSelected)

                                  formikBag.setFieldValue(
                                    'items',
                                    (formikBag.values.items || []).filter((_, i) => i !== index)
                                  )

                                  // clear dependent selections
                                  formikBag.setFieldValue('courier', null)
                                  formikBag.setFieldValue('payment_method_list', [''])
                                  formikBag.setFieldValue('payment_method', null)
                                  formikBag.setFieldValue('card_type', null)
                                  formikBag.setFieldValue('payment_transfer_receipts', [])
                                }}
                              >
                                Delete Item
                              </Button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </>
                )}
              />

              <hr />

              {/* =========================
                    6) PICKUP DATE + METODE PENGIRIMAN
                 ========================= */}
              <SectionTitle
                title='Pickup Date & Metode Pengiriman'
                subtitle={<>Pickup Date <b>opsional</b>. Kurir wajib dipilih setelah item & Final Price lengkap.</>}
              />

              <DateInputField
                label='Pickup Date (Opsional)'
                name='pickup_at'
                dateFormat='dd/MM/yyyy'
                required={ false }
                formText='📦 Tanggal estimasi barang diambil/dikirim. Boleh dikosongkan jika belum pasti. Format: dd/mm/yy'
              />
              {hasAtLeastOneItem && allItemsComplete ? (
                <>
                  <div data-required-field='courier'>
                    <CourierSelectSearchField
                      name='courier'
                      label='Kurir'
                      required={ true }
                      latitude={formikBag.values.latitude}
                      longitude={formikBag.values.longitude}
                      productIdWithSellingPriceList={(items || []).map(o => ({
                        product_id: o.product_id,
                        selling_price: parseFloat(o.selling_price),
                      }))}
                      formText='Tip: isi koordinat alamat agar opsi kurir lebih lengkap.'
                    />
                  </div>

                  {formikBag.values.courier && (
                    <ThousandsInputField
                      name='shipment_cost'
                      label='Ongkir'
                      required={ false }
                      placeholder='Masukkan nominal ongkir'
                      formText='Opsional. Nominal ongkir diinput manual sesuai kondisi di lapangan. Isi 0 jika tidak ada ongkir.'
                    />
                  )}
                </>
              ) : (
                <Alert color='secondary'>
                  Kurir akan muncul setelah:<br/>
                  ✔️ Minimal 1 item ditambahkan<br/>
                  ✔️ Semua Final Price terisi
                </Alert>
              )}

              <hr />

              {/* =========================
                    7) ORDER EXTRAS + NOTE
                 ========================= */}
              <SectionTitle
                title='Order'
                subtitle={<>Tambahan order & catatan internal.</>}
              />

              <ToggleField type='checkbox' name='include_invoice' label='Include Invoice' />
              <ToggleField type='checkbox' name='include_polaroid' label='Include Polaroid' />
              <ToggleField type='checkbox' name='include_love_letter' label='Include Surat Cinta' />

              <InputField
                type='text'
                name='voucher_code'
                label='Kode Voucher (opsional)'
                placeholder='NEW500'
                formText='Isi jika pembelian menggunakan voucher'
              />

              <InputField
                type='text'
                name='note'
                label='Catatan (opsional)'
                placeholder='Catatan'
                formText='Catatan internal untuk order/deal'
              />

              <ThousandsInputField
                name='down_payment'
                label={`Down Payment (DP) ${isMidtransCCSelected ? '(Opsional untuk MIDTRANS CC)' : '(Wajib)'}`}
                placeholder=''
                required={!isMidtransCCSelected}
                formText={
                  isMidtransCCSelected
                    ? 'Untuk MIDTRANS (CC), DP boleh dikosongkan. Jika diisi, nilainya harus lebih dari 0.'
                    : 'DP wajib diisi untuk create deals ready. Nilai harus lebih dari 0.'
                }
              />

              <div 
                className='mb-3'
                style={{
                border: formikBag.errors?.dp_transfer_proofs && formikBag.touched?.dp_transfer_proofs ? '1px solid #dc3545' : '1px solid transparent',
                borderRadius: 6,
                padding: formikBag.errors?.dp_transfer_proofs && formikBag.touched?.dp_transfer_proofs ? 10 : 0,
                background: formikBag.errors?.dp_transfer_proofs && formikBag.touched?.dp_transfer_proofs ? '#fff5f5' : 'transparent',
                }}
              >
                <Label className='form-control-label' style={{ fontWeight: 700 }}>
                  Bukti Transfer DP {isMidtransCCSelected ? '(Opsional untuk MIDTRANS CC)' : '(Wajib)'}
                </Label>
                <Input
                  name='dp_transfer_proofs'
                  type='file'
                  multiple
                  accept='image/*,application/pdf'
                  onChange={(e) => {
                    const files = Array.from(e?.currentTarget?.files || [])

                    // NEW: requirement user -> limit upload dinaikkan jadi 10 file
                    const MAX_FILES = 10
                    const MAX_SIZE_MB = 5
                    const ALLOWED_TYPES = [
                      'image/jpeg',
                      'image/png',
                      'application/pdf'
                    ]

                    const validFiles = []

                    for (let file of files) {
                      if (!ALLOWED_TYPES.includes(file.type)) {
                        alert(`File ${file.name} tidak didukung`)
                        continue
                      }

                      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                        alert(`File ${file.name} melebihi ${MAX_SIZE_MB}MB`)
                        continue
                      }

                      validFiles.push(file)
                    }

                    if (validFiles.length > MAX_FILES) {
                      alert(`Maksimal upload ${MAX_FILES} file`)
                      validFiles.splice(MAX_FILES)
                    }

                    formikBag.setFieldValue('dp_transfer_proofs', validFiles)
                    formikBag.setFieldTouched('dp_transfer_proofs', true, false)
                  }}
                />
                <FormText className='text-muted'>
                  {isMidtransCCSelected
                    ? 'Untuk MIDTRANS (CC), upload bukti transfer DP tidak wajib. DP boleh dikosongkan. Jika diisi, nilainya harus lebih dari 0. Upload bisa lebih dari 1 file (Max 10 file) dengan format (JPG/JPEG/PNG/PDF) Max 2MB.'
                    : 'Upload screenshot / bukti pembayaran DP. Upload ini wajib. Upload bisa lebih dari 1 file (Max 10 file) dengan format (JPG/JPEG/PNG/PDF) Max 2MB.'}
                </FormText>

                {formikBag.errors?.dp_transfer_proofs && formikBag.touched?.dp_transfer_proofs && (
                  <div style={{ color: '#dc3545', marginTop: 6 }}>
                    {formikBag.errors.dp_transfer_proofs}
                  </div>
                )}

                {(formikBag.values.dp_transfer_proofs || []).length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <Label style={{ fontWeight: 600 }}>Preview Bukti Transfer DP</Label>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {(formikBag.values.dp_transfer_proofs || []).map((file, idx) => {
                        const isPdf = file?.type?.includes('pdf')
                        const previewUrl = URL.createObjectURL(file) 

                        return (
                          <div
                            key={`${file.name}-${idx}`}
                            style={{
                              border: '1px solid #ddd',
                              borderRadius: 6,
                              padding: 10,
                              position: 'relative',
                            }}
                          >
                            <Button
                              size='sm'
                              color='danger'
                              type='button'
                              style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                padding: '2px 8px',
                              }}
                              onClick={() => {
                                const updated = [...(formikBag.values.dp_transfer_proofs || [])]
                                updated.splice(idx, 1)
                                formikBag.setFieldValue('dp_transfer_proofs', updated)
                              }}
                            >
                            </Button>

                            {isPdf ? (
                              <>
                                <div style={{ fontWeight: 600 }}>📄 PDF File</div>
                                <div style={{ fontSize: 13 }}>{file.name}</div>
                              </>
                            ) : (
                              <>
                                <img
                                  src={previewUrl}
                                  alt={`dp-proof-${idx}`}
                                  style={{
                                    width: '100%',
                                    maxHeight: 320,
                                    objectFit: 'contain',
                                    borderRadius: 4,
                                  }}
                                />
                                <div style={{ fontSize: 12, marginTop: 6 }}>
                                  {file.name}
                                </div>
                              </>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                   )}
              </div>

              <hr />

              {/* =========================
                    8) PAYMENT METHOD (LAST)
                 ========================= */}
              <SectionTitle
                title='Metode Pembayaran'
                subtitle={<>Metode pembayaran baru bisa dipilih setelah kurir dipilih.</>}
              />

              {formikBag.values.courier ? (
                <>
                  <FieldArray
                    name='payment_method_list'
                    render={(arrayHelpers) => {
                      const list = formikBag.values.payment_method_list || ['']

                      return (
                        <>
                          {list.map((_, idx) => (
                            <div key={`pm_${idx}`} className='border rounded p-2 mb-2'>
                              <div data-required-field={`payment_method_list.${idx}`}>
                                <PaymentMethodSelectSearchField
                                  name={`payment_method_list.${idx}`}
                                  label={`Metode Pembayaran ${idx + 1}`}
                                  required={idx === 0}
                                  variant='deal_v2'
                                />
                              </div>

                              {idx > 0 && (
                                <Button
                                  type='button'
                                  color='danger'
                                  size='sm'
                                  onClick={() => arrayHelpers.remove(idx)}
                                >
                                  Hapus Metode
                                </Button>
                              )}
                            </div>
                          ))}

                          <Button type='button' color='secondary' onClick={() => arrayHelpers.push('')}>
                            + Tambah Metode Pembayaran
                          </Button>

                          <FormText className='text-muted mt-2'>
                            Catatan: untuk sementara backend lama tetap memakai <b>metode pembayaran pertama</b>.
                            Setelah backend multi-payments aktif, list ini bisa dipakai bikin Payment #1/#2/#3.
                          </FormText>

                          <FormText className='text-muted mt-1'>
                            Untuk Midtrans CC, pilih langsung opsi <b>Non Amex</b> atau <b>Amex</b> di list metode pembayaran.
                          </FormText>
                        </>
                      )
                    }}
                  />

                  {showCardType && (
                    <div className='mt-3'>
                      <InputField
                        type='select'
                        name='card_type'
                        label='Card Type'
                        required={ true }
                      >
                        <option value=''>-- pilih card type --</option>
                        {CARD_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </InputField>

                      <FormText className='text-muted'>
                        Card Type hanya berlaku untuk <b>Metode Pembayaran 1</b> karena backend lama
                        masih membaca payment pertama.
                      </FormText>
                    </div>
                  )}

                  <div className='mt-3 mb-3'>
                    <Label className='form-control-label' style={{ fontWeight: 700 }}>
                      Upload Bukti Bayar Tambahan / Pelunasan (Opsional)
                    </Label>
                    <Input
                      type='file'
                      multiple
                      accept='image/*,application/pdf'
                      onChange={(e) => {
                        const files = Array.from(e?.currentTarget?.files || [])

                        // NEW: requirement user -> limit upload dinaikkan jadi 10 file
                        const MAX_FILES = 10
                        const MAX_SIZE_MB = 5
                        const ALLOWED_TYPES = [
                          'image/jpeg',
                          'image/png',
                          'application/pdf'
                        ]

                        const validFiles = []

                        for (let file of files) {
                          if (!ALLOWED_TYPES.includes(file.type)) {
                            alert(`File ${file.name} tidak didukung`)
                            continue
                          }

                          if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                            alert(`File ${file.name} melebihi ${MAX_SIZE_MB}MB`)
                            continue
                          }

                          validFiles.push(file)
                        }

                        if (validFiles.length > MAX_FILES) {
                          alert(`Maksimal upload ${MAX_FILES} file`)
                          validFiles.splice(MAX_FILES)
                        }

                        formikBag.setFieldValue('payment_transfer_receipts', validFiles)
                        formikBag.setFieldTouched('payment_transfer_receipts', true, false)
                      }}
                    />
                    <FormText className='text-muted'>
                      Field ini tetap dipertahankan untuk bukti bayar tambahan / pelunasan.
                      Upload bisa lebih dari 1 file (Max 10 file) dengan format (JPG/JPEG/PNG/PDF) Max 5MB.
                    </FormText>

                    {formikBag.errors?.payment_transfer_receipts && (
                      <div style={{ color: '#dc3545', marginTop: 6 }}>
                        {formikBag.errors.payment_transfer_receipts}
                      </div>
                    )}

                    {(formikBag.values.payment_transfer_receipts || []).length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <Label style={{ fontWeight: 600 }}>Preview Bukti Pelunasan</Label>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                          {(formikBag.values.payment_transfer_receipts || []).map((file, idx) => {
                            const isPdf = file?.type?.includes('pdf')
                            const previewUrl = URL.createObjectURL(file)

                            return (
                              <div
                                key={`${file.name}-${idx}`}
                                style={{
                                  border: '1px solid #ddd',
                                  borderRadius: 6,
                                  padding: 10,
                                  position: 'relative',
                                }}
                              >
                                <Button
                                  size='sm'
                                  color='danger'
                                  type='button'
                                  style={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    padding: '2px 8px',
                                  }}
                                  onClick={() => {
                                    const updated = [...(formikBag.values.payment_transfer_receipts || [])]
                                    updated.splice(idx, 1)
                                    formikBag.setFieldValue('payment_transfer_receipts', updated)
                                  }}
                                >
                                </Button>

                                {isPdf ? (
                                  <>
                                    <div style={{ fontWeight: 600 }}>📄 PDF File</div>
                                    <div style={{ fontSize: 13 }}>{file.name}</div>
                                  </>
                                ) : (
                                  <>
                                    <img
                                      src={previewUrl}
                                      alt={`payment-receipt-${idx}`}
                                      style={{
                                        width: '100%',
                                        maxHeight: 320,
                                        objectFit: 'contain',
                                        borderRadius: 4,
                                      }}
                                    />
                                    <div style={{ fontSize: 12, marginTop: 6 }}>
                                      {file.name}
                                    </div>
                                  </>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <Alert color='secondary'>Pilih Kurir dulu untuk lanjut pilih metode pembayaran.</Alert>
              )}

              <br />

              {submitErrorSummary.length > 0 && (
                <Alert color='danger'>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    Mohon lengkapi data wajib berikut:
                  </div>

                  <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                    {submitErrorSummary.map((msg, idx) => (
                      <li key={`${msg}-${idx}`}>{msg}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              <Button
                color='primary'
                disabled={formikBag.isSubmitting}
                onClick={async () => {
                  const errors = await formikBag.validateForm()

                  const touchedFromErrors = buildTouchedFromErrors(errors)
                  formikBag.setTouched(
                    mergeDeep(formikBag.touched, touchedFromErrors),
                    false
                  )

                  if (Object.keys(errors).length > 0) {
                    setSubmitErrorSummary(buildSubmitErrorSummary(errors))

                    const firstErrorField = getFirstErrorField(errors)
                    const firstErrorLabel = getFieldLabel(firstErrorField)

                    onValidationError(firstErrorLabel)
                    scrollToFirstInvalidField(firstErrorField)

                    return
                  }

                  setSubmitErrorSummary([])
                  clearSubmitErrorTarget()
                  formikBag.submitForm()
                }}
              >
                {'Create Deal'}
              </Button>
            </Form>
          </>
        )
      }}
    </Formik>
  )
}

SalesDealsCreateForm.propTypes = propTypes
export default SalesDealsCreateForm
